import Vue from 'vue'
import { validationMixin } from 'vuelidate'
import { email, required, requiredIf } from 'vuelidate/lib/validators'
import { alphaWithSpaces, isTrue, isDateValidWithDashes } from 'utils/validators'
import PaxSubscribe from 'services/AirBaltic/Pax/Subscribe'
import ErrorHandler from 'utils/ErrorHandler'

const SubscribeFormState = function () {
  const defaultState = {
    subscribeSource: '',
    interest: '',
    title: '',
    description: '',
    termsDescription: '',
    sref: '',
    image: '',
    refName: '',
    fieldErrors: [],
    genericErrors: [],
    preferredLanguages: [],
    residenceCountries: [],
    destinations: [],
    formData: {
      email: '',
      preferredLanguage: '',
      countryOfResidence: '',
      gender: '',
      name: '',
      surname: '',
      homeAirport: '',
      dreamDestination: '',
      terms: false,
      dob: '',
      birthDate: '',
      birthMonth: '',
      birthYear: '',
      deviceType: ''
    },
    formFieldVisibility: {
      email: true,
      preferredLanguage: false,
      countryOfResidence: false,
      gender: false,
      name: false,
      surname: false,
      homeAirport: false,
      dreamDestination: false,
      terms: false,
      dob: false
    },
    requiredFields: {
      email: true,
      preferredLanguage: false,
      countryOfResidence: false,
      gender: false,
      name: false,
      surname: false,
      homeAirport: false,
      dreamDestination: false,
      terms: false,
      dob: false
    }
  }

  const validator = new Vue({
    mixins: [
      validationMixin
    ],
    computed: {
      state () {
        return defaultState
      }
    },
    validations () {
      const validationRules = {
        preferredLanguage: {
          required: requiredIf(() => this.state.requiredFields.preferredLanguage)
        },
        countryOfResidence: {
          required: requiredIf(() => this.state.requiredFields.countryOfResidence)
        },
        gender: {
          required: requiredIf(() => this.state.requiredFields.gender)
        },
        homeAirport: {
          required: requiredIf(() => this.state.requiredFields.homeAirport)
        },
        dreamDestination: {
          required: requiredIf(() => this.state.requiredFields.dreamDestination)
        },
        email: { required, email }
      }

      if (this.state.formFieldVisibility.terms) {
        // eslint-disable-next-line object-curly-spacing
        Object.assign(validationRules, { terms: { required, isTrue } })
      }

      if (this.state.formFieldVisibility.name) {
        if (this.state.requiredFields.name) {
          // eslint-disable-next-line object-curly-spacing
          Object.assign(validationRules, { name: { required, alphaWithSpaces } })
        } else {
          // eslint-disable-next-line object-curly-spacing
          Object.assign(validationRules, { name: { alphaWithSpaces } })
        }
      }

      if (this.state.formFieldVisibility.surname) {
        if (this.state.requiredFields.surname) {
          // eslint-disable-next-line object-curly-spacing
          Object.assign(validationRules, { surname: { required, alphaWithSpaces } })
        } else {
          // eslint-disable-next-line object-curly-spacing
          Object.assign(validationRules, { surname: { alphaWithSpaces } })
        }
      }

      if (this.state.formFieldVisibility.dob) {
        // eslint-disable-next-line object-curly-spacing
        Object.assign(validationRules, { dob: { isValidDate: isDateValidWithDashes } })
      }

      return {
        state: {
          formData: validationRules
        }
      }
    }
  })

  const module = {
    namespaced: true,
    state: defaultState,
    getters: {
      $v () {
        return Object.assign({}, validator.$v.state.formData)
      },
      formErrors (state) {
        let errors = []
        errors = errors.concat(state.fieldErrors, state.genericErrors)

        return errors
      }
    },
    mutations: {
      setSubscribeSource (state, value) {
        state.subscribeSource = value
      },
      setTitle (state, value) {
        state.title = value
      },
      addError (state, errorKey) {
        if (state.fieldErrors.indexOf(errorKey) === -1) {
          state.fieldErrors.push(errorKey)
        }
      },
      removeErrors (state) {
        state.fieldErrors = []
        state.genericErrors = []
      },
      setInterest (state, value) {
        state.interest = value
      },
      setSref (state, value) {
        state.sref = value
      },
      setImage (state, value) {
        state.image = value
      },
      setDescription (state, value) {
        state.description = value
      },
      setRefName (state, value) {
        state.refName = value
      },
      setVisibleField (state, value) {
        state.formFieldVisibility[`${value}`] = true
      },
      setPreferredLanguages (state, value) {
        state.preferredLanguages = value
      },
      setPreferredLanguage (state, value) {
        state.formData.preferredLanguage = value
      },
      setResidenceCountries (state, value) {
        state.residenceCountries = value
      },
      setCountryOfResidence (state, value) {
        state.formData.countryOfResidence = value
      },
      setGender (state, value) {
        state.formData.gender = value
      },
      setName (state, value) {
        state.formData.name = value
      },
      setDob (state, value) {
        state.formData.dob = value
      },
      setSurname (state, value) {
        state.formData.surname = value
      },
      setHomeAirport (state, value) {
        state.formData.homeAirport = value
      },
      setDreamDestination (state, value) {
        state.formData.dreamDestination = value
      },
      setDestinations (state, value) {
        state.destinations = value
      },
      setTermsDescription (state, value) {
        state.termsDescription = value
      },
      setTerms (state, value) {
        state.formData.terms = value
      },
      setEmail (state, value) {
        state.formData.email = typeof value === 'string' ? value.replace(/ /g, '') : ''
      },
      setRequiredField (state, value) {
        state.requiredFields[`${value}`] = true
      },
      setMissingTermsText (state, value) {
        state.missingTermsText = value
      },
      setBirthDate (state, value) {
        state.formData.birthDate = value
      },
      setBirthMonth (state, value) {
        state.formData.birthMonth = value
      },
      setBirthYear (state, value) {
        state.formData.birthYear = value
      }
    },
    actions: {
      setBirthDate ({ commit, dispatch }, data) {
        commit('setBirthDate', data)
        dispatch('validateDate')
      },
      setBirthMonth ({ commit, dispatch }, data) {
        commit('setBirthMonth', data)
        dispatch('validateDate')
      },
      setBirthYear ({ commit, dispatch }, data) {
        commit('setBirthYear', data)
        dispatch('validateDate')
      },
      validateDate ({ commit, state }) {
        const birthDate = state.formData.birthDate
        const birthMonth = state.formData.birthMonth
        const birthYear = state.formData.birthYear

        let date = `${birthYear}-${birthMonth}-${birthDate}`
        if (birthDate === '' || birthMonth === '' || birthYear === '') {
          date = ''
        }

        commit('setDob', date)
      },
      setVisibleFields ({ commit, state }, fields) {
        fields.forEach((fieldName) => {
          if (fieldName in state.formData) {
            commit('setVisibleField', fieldName)
          }
        })
      },
      setRequiredFields ({ commit, state }, fields) {
        fields.forEach((fieldName) => {
          if (fieldName in state.formData) {
            commit('setRequiredField', fieldName)
          }
        })
      },
      submitForm ({ commit, state }) {
        return new Promise((resolve, reject) => {
          const PaxSubscribeService = new PaxSubscribe()
          const subscriber = {
            tag: state.interest,
            subscribeSource: state.subscribeSource + state.sref,
            email: state.formData.email
          }

          if (state.formFieldVisibility.preferredLanguage) {
            Object.assign(subscriber, { language: state.formData.preferredLanguage.toUpperCase() })
          }

          if (state.formFieldVisibility.countryOfResidence) {
            Object.assign(subscriber, { country: state.formData.countryOfResidence.toUpperCase() })
          }

          if (state.formFieldVisibility.gender) {
            Object.assign(subscriber, { gender: state.formData.gender.toUpperCase() })
          }

          if (state.formFieldVisibility.name) {
            Object.assign(subscriber, { firstName: state.formData.name })
          }

          if (state.formFieldVisibility.title) {
            Object.assign(subscriber, { title: state.formData.title })
          }

          if (state.formFieldVisibility.surname) {
            Object.assign(subscriber, { lastName: state.formData.surname })
          }

          if (state.formFieldVisibility.homeAirport) {
            Object.assign(subscriber, { homePort: state.formData.homeAirport })
          }

          if (state.formFieldVisibility.dreamDestination) {
            Object.assign(subscriber, { dreamPort: state.formData.dreamDestination })
          }

          if (state.formFieldVisibility.dob) {
            Object.assign(subscriber, { dateOfBirth: state.formData.dob })
          }

          Object.keys(subscriber)
            .forEach(key => (subscriber[`${key}`] === '') && delete subscriber[`${key}`])

          PaxSubscribeService.subscribeWithApi(subscriber)
            .then(() => resolve('subscribed'))
            .catch((e) => {
              if (e.data.code === 'ALREADY_SUBSCRIBED') {
                return resolve('already')
              } else {
                commit('addError', ErrorHandler.getErrorTranslationKey([], e))
                return reject(false)
              }
            })
        })
      },
      validate ({ commit, state }) {
        return new Promise((resolve) => {
          validator.$v.state.formData.$touch()
          const form = validator.$v.state.formData
          commit('removeErrors')
          if (form.email && form.email.required === false) {
            commit('addError', 'reusable.email.validation.missing')
          }
          if (form.email && form.email.email === false) {
            commit('addError', 'reusable.email.validation.invalid')
          }
          if (form.preferredLanguage && form.preferredLanguage.required === false) {
            commit('addError', 'snippetTemplate.preferredLanguage.preferredLanguageRequired')
          }
          if (form.countryOfResidence && form.countryOfResidence.required === false) {
            commit('addError', 'snippetTemplate.countryOfResidence.countryOfResidenceRequired')
          }
          if (form.gender && form.gender.required === false) {
            commit('addError', 'snippetTemplate.gender.genderRequired')
          }
          if (form.name && form.name.required === false) {
            commit('addError', 'snippetTemplate.name.nameRequired')
          }
          if (form.name && form.name.alphaWithSpaces === false) {
            commit('addError', 'snippetTemplate.name.nameAlphaWithSpaces')
          }
          if (form.surname && form.surname.required === false) {
            commit('addError', 'snippetTemplate.surname.surnameRequired')
          }
          if (form.surname && form.surname.alphaWithSpaces === false) {
            commit('addError', 'snippetTemplate.surname.surnameAlphaWithSpaces')
          }
          if (form.homeAirport && form.homeAirport.required === false) {
            commit('addError', 'snippetTemplate.homeAirport.homeAirportRequired')
          }
          if (form.dreamDestination && form.dreamDestination.required === false) {
            commit('addError', 'snippetTemplate.dreamDestination.dreamDestinationRequired')
          }
          if (form.terms && (form.terms.required === false || form.terms.isTrue === false)) {
            commit('addError', state.missingTermsText)
          }
          if (form.dob && form.dob.isValidDate === false) {
            commit('addError', 'searchForm.dateSelection.errors.invalid')
          }

          resolve(!form.$invalid)
        })
      }
    }
  }

  return module
}

export default SubscribeFormState
